'use client';

import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import { Button } from '@kit/ui/button';
import {
  ResponsivePopup,
  ResponsivePopupContent,
} from '@kit/ui/responsive-popup';

import {
  type UserType,
  isIndividualType,
} from '../../schema/professional-profile.schema';
import {
  type AccountCategory,
  AccountCategoryStep,
} from './account-category-step';
import { getTrialPeriodDays } from './constants';
import { WIZARD_SUBTLE_BUTTON, WizardFooterBar } from './form-components';
import { IndividualDetailsStep } from './individual-details-step';
import { OrganizationDetailsStep } from './organization-details-step';
import { UserTypeStep } from './user-type-step';

interface ProfileWizardProps {
  open: boolean;
  userId: string;
  onComplete: () => void;
  onSkip?: () => void;
}

type WizardStep = 'category' | 'type' | 'details';

export function ProfileWizard({
  open,
  userId,
  onComplete,
  onSkip,
}: ProfileWizardProps) {
  const { t } = useTranslation('account');
  const [step, setStep] = useState<WizardStep>('category');
  const [selectedCategory, setSelectedCategory] =
    useState<AccountCategory | null>(null);
  const [selectedType, setSelectedType] = useState<UserType | null>(null);

  const handleCategorySelect = (category: AccountCategory) => {
    setSelectedCategory(category);
    setStep('type');
  };

  const handleTypeSelect = (type: UserType) => {
    setSelectedType(type);
    setStep('details');
  };

  const handleBackToCategory = () => {
    setSelectedCategory(null);
    setStep('category');
  };

  const handleBackToType = () => {
    setStep('type');
  };

  const handleComplete = () => {
    onComplete();
  };

  const handleSkip = () => {
    onSkip?.();
  };

  return (
    <ResponsivePopup
      desktop="dialog"
      open={open}
      // ✕, Escape and clicking outside all mean "skip for now"
      onOpenChange={(next) => {
        if (!next) handleSkip();
      }}
    >
      <ResponsivePopupContent
        title={t('profileWizard.title', { days: getTrialPeriodDays() })}
        description={t('profileWizard.subtitle')}
        desktopClassName="max-w-[calc(100vw-2rem)] rounded-lg sm:max-w-[500px]"
      >
        {step === 'category' && (
          <AccountCategoryStep onSelect={handleCategorySelect} />
        )}

        {step === 'type' && selectedCategory && (
          <UserTypeStep
            category={selectedCategory}
            onSelect={handleTypeSelect}
            onBack={handleBackToCategory}
          />
        )}

        {step === 'details' && selectedType && (
          <>
            {isIndividualType(selectedType) ? (
              <IndividualDetailsStep
                userType={selectedType}
                userId={userId}
                onBack={handleBackToType}
                onComplete={handleComplete}
              />
            ) : (
              <OrganizationDetailsStep
                userType={selectedType}
                userId={userId}
                onBack={handleBackToType}
                onComplete={handleComplete}
              />
            )}
          </>
        )}

        {/* Footer with skip option */}
        {step === 'category' && (
          <WizardFooterBar className="justify-end border-t">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleSkip}
              className={WIZARD_SUBTLE_BUTTON}
            >
              {t('profileWizard.skipForNow')}
            </Button>
          </WizardFooterBar>
        )}
      </ResponsivePopupContent>
    </ResponsivePopup>
  );
}
